import { useCallback, useEffect, useMemo, useState } from 'react';
import EditLeagueModal from './components/EditLeagueModal';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { openAuth } from '../../redux/slice/authModalSlice';
import { setSelectedLeague } from '../../redux/slice/selectedLeagueSlice';
// import { setActiveTab } from '../../redux/slice/activeTabSlice';
import { getAuthToken, getGuestModeFromStorage } from '../../helper/helper';
import { Images } from '../../utils/imageConstants';
import { fetchAllLeagues, mapLeagueItemToGetAllLeagues } from './api/myLeaguesApi';
import type { ILeagueItem } from './types/myLeagues';
import MyLeaguesEmptyState from './components/MyLeaguesEmptyState';
import LeagueList from './components/LeagueList';

type LeagueTab = 'all' | 'created' | 'participated';

const LEAGUES_PER_PAGE = 6;
const CREATOR_ROLES = ['ORGANIZER', 'CREATOR', 'ADMIN', 'OWNER'];

function isCreatorLeague(league: ILeagueItem): boolean {
  const role = (league.roleName ?? '').toUpperCase();
  const hasCreatorPermissions = Boolean(
    league.canEditLeague ||
    league.canAddTournamentToLeague ||
    league.canAddTeamToLeague ||
    league.canAddPlayerToTeam ||
    league.canCreateDivisions
  );

  return hasCreatorPermissions || CREATOR_ROLES.some((r) => role.includes(r));
}

function MyLeagues() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isGuestModeRedux = useAppSelector((s) => s.authModal.isGuestMode);
  const isGuestUser = isGuestModeRedux || getGuestModeFromStorage();
  /** All / Created / Participated — must not shadow Redux `setActiveTab` (league shell tab). */
  const [listFilter, setListFilter] = useState<LeagueTab>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [leagueToEdit, setLeagueToEdit] = useState<ILeagueItem | null>(null);

  const { data: leagues = [], isLoading, refetch } = useQuery({
    queryKey: ['myLeagues', 'allLeague'],
    queryFn: fetchAllLeagues,
    staleTime: 30_000,
  });

  const filteredLeagues = useMemo(() => {
    if (listFilter === 'all') return leagues;
    if (listFilter === 'created') return leagues.filter(isCreatorLeague);
    return leagues.filter((l) => !isCreatorLeague(l));
  }, [leagues, listFilter]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredLeagues.length / LEAGUES_PER_PAGE)),
    [filteredLeagues.length]
  );

  const startIndex = (currentPage - 1) * LEAGUES_PER_PAGE;
  const paginatedLeagues = useMemo(() => {
    return filteredLeagues.slice(startIndex, startIndex + LEAGUES_PER_PAGE);
  }, [filteredLeagues, startIndex]);

  const getPageNumbers = (): (number | 'ellipsis')[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages: (number | 'ellipsis')[] = [1];
    if (currentPage > 3) pages.push('ellipsis');
    const midStart = Math.max(2, currentPage - 1);
    const midEnd = Math.min(totalPages - 1, currentPage + 1);
    for (let i = midStart; i <= midEnd; i++) {
      if (i !== 1 && i !== totalPages) pages.push(i);
    }
    if (currentPage < totalPages - 2) pages.push('ellipsis');
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [listFilter]);

  useEffect(() => {
    if (isGuestUser && listFilter !== 'all') setListFilter('all');
  }, [isGuestUser, listFilter]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const handleCreateLeague = useCallback(() => {
    if (!getAuthToken()) {
      toast.error('Please login or signup to create a league.');
      dispatch(openAuth('signin'));
      return;
    }
    navigate('/create-league');
  }, [navigate, dispatch]);

  const handleLeagueSelect = useCallback(
    (league: ILeagueItem) => {
      dispatch(setSelectedLeague(mapLeagueItemToGetAllLeagues(league)));
      navigate(`/my-leagues/${league.leagueId}`);
    },
    [dispatch, navigate]
  );

  const handleEditLeague = useCallback((league: ILeagueItem) => {
    // Defer open until after the triggering click finishes. Otherwise Radix Dialog
    // treats the same pointer event as an outside press and closes immediately.
    window.setTimeout(() => setLeagueToEdit(league), 0);
  }, []);

  const tabs: { id: LeagueTab; label: string }[] = useMemo(
    () =>
      isGuestUser
        ? [{ id: 'all', label: 'All League' }]
        : [
          { id: 'all', label: 'All League' },
          { id: 'created', label: 'Created' },
          { id: 'participated', label: 'Participated' },
        ],
    [isGuestUser]
  );

  const isEmpty = filteredLeagues.length === 0;

  return (
    <div className="my-leagues">
      <div className="myleagues-container">
        <div className="myleagues-header">
          <div className="myleagues-header-left">
            <h1 className="myleagues-title">Leagues</h1>
            <p className="myleagues-subtitle">Browse and Join Leagues</p>
          </div>
          <button
            type="button"
            className="myleagues-create-btn"
            onClick={handleCreateLeague}
          >
            <img src={Images.add_icon} alt="" aria-hidden />
            <span>Create League</span>
          </button>
        </div>

        {tabs.length > 1 && (
          <div className="myleagues-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`myleagues-tab ${listFilter === tab.id ? 'active' : ''}`}
                onClick={() => setListFilter(tab.id)}
              >
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        )}

        <div className={`myleagues-content ${isEmpty ? 'myleagues-content-empty' : ''}`}>
          {isLoading ? (
            <div className="myleagues-loading">
              <span>Loading leagues...</span>
            </div>
          ) : isEmpty ? (
            <MyLeaguesEmptyState onCreateLeague={handleCreateLeague} />
          ) : (
            <>
              <LeagueList
                leagues={paginatedLeagues}
                onLeagueSelect={handleLeagueSelect}
                onEditLeague={!isGuestUser ? handleEditLeague : undefined}
                showRoleBadge={!isGuestUser}
              />
              {totalPages > 1 && (
                <div className="myleagues-pagination">
                  <span className="myleagues-pagination-info">
                    Showing {startIndex + 1}–{Math.min(startIndex + LEAGUES_PER_PAGE, filteredLeagues.length)} of {filteredLeagues.length} leagues
                  </span>
                  <button
                    type="button"
                    className="myleagues-pagination-btn"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    Previous
                  </button>
                  <div className="myleagues-pagination-pages">
                    {getPageNumbers().map((page, i) =>
                      page === 'ellipsis' ? (
                        <span key={`ellipsis-${i}`} className="myleagues-pagination-ellipsis">
                          …
                        </span>
                      ) : (
                        <button
                          key={page}
                          type="button"
                          className={`myleagues-pagination-page ${currentPage === page ? 'active' : ''}`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      )
                    )}
                  </div>
                  <button
                    type="button"
                    className="myleagues-pagination-btn"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <EditLeagueModal
        open={!!leagueToEdit}
        league={leagueToEdit}
        onOpenChange={(o) => {
          if (!o) setLeagueToEdit(null);
        }}
        onUpdated={() => void refetch()}
      />
    </div>
  );
}

export default MyLeagues;
